import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAI } from '@/context/AIContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Sparkles, Loader2, Printer, AlertCircle, Calendar, Users, MessageSquare, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const MEETING_TYPES = [
  'Annual review', 'Quarterly check-in', 'Tax planning', 'Investment review',
  'Estate / succession planning', 'Insurance review', 'Compliance review',
  'Initial strategy', 'Family meeting', 'Ad-hoc',
];

export default function MeetingPrepButton({ clientId, clientName }) {
  const { token } = useAuth();
  const { aiEnabled } = useAI();
  const [open, setOpen] = useState(false);
  const [meetingType, setMeetingType] = useState('Annual review');
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);

  if (!aiEnabled) return null;

  const generate = async () => {
    setLoading(true);
    setBrief(null);
    try {
      const res = await api.post(token, '/ai/agents/meeting-prep', {
        clientId, meetingType,
      });
      setBrief(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate prep brief');
    }
    setLoading(false);
  };

  const printBrief = () => {
    const w = window.open('', '_blank');
    w.document.write(`
      <html><head><title>Meeting Prep — ${clientName}</title>
      <style>
        body{font-family:-apple-system,sans-serif;max-width:800px;margin:40px auto;padding:0 20px;line-height:1.5;color:#2C2420}
        h1{font-size:24px;margin-bottom:6px}h2{font-size:14px;text-transform:uppercase;color:#8C8078;margin-top:24px;margin-bottom:8px;letter-spacing:0.5px}
        h3{font-size:13px;color:#5D524D;margin:0}.meta{color:#8C8078;font-size:12px;margin-bottom:24px}
        ul{padding-left:20px;margin:8px 0}li{margin-bottom:4px}
        .summary{font-size:15px;color:#2C2420;background:#FFF8F0;padding:16px;border-radius:8px;border-left:3px solid #A86A3A}
        .item{padding:8px 0;border-bottom:1px solid #F0EBE6}.tag{display:inline-block;padding:2px 6px;background:#F0EBE6;border-radius:4px;font-size:11px;color:#4A3B32;margin-right:6px}
      </style></head><body>
        <h1>${clientName}</h1>
        <p class="meta">${meetingType} — Generated ${new Date().toLocaleDateString('en-AU', {day:'numeric',month:'short',year:'numeric'})}</p>
        <div class="summary">${brief.summary || ''}</div>
        ${brief.keyContacts?.length ? `<h2>Key Contacts</h2>${brief.keyContacts.map(c => `<div class="item"><h3>${c.name} <span class="tag">${c.role}</span></h3><div>${c.note||''}</div></div>`).join('')}` : ''}
        ${brief.openItems?.length ? `<h2>Open Items</h2>${brief.openItems.map(o => `<div class="item"><strong>${o.title}</strong> <span class="tag">${o.status}</span> ${o.dueDate ? `<span>${o.dueDate}</span>` : ''}</div>`).join('')}` : ''}
        ${brief.talkingPoints?.length ? `<h2>Talking Points</h2><ul>${brief.talkingPoints.map(p => `<li>${p}</li>`).join('')}</ul>` : ''}
        ${brief.risks?.length ? `<h2>Risks</h2><ul>${brief.risks.map(p => `<li>${p}</li>`).join('')}</ul>` : ''}
        ${brief.recentActivity?.length ? `<h2>Recent Activity</h2><ul>${brief.recentActivity.map(p => `<li>${p}</li>`).join('')}</ul>` : ''}
        ${brief.suggestedQuestions?.length ? `<h2>Suggested Questions</h2><ul>${brief.suggestedQuestions.map(p => `<li>${p}</li>`).join('')}</ul>` : ''}
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 250);
  };

  return (
    <>
      <Button
        variant="outline" size="sm"
        onClick={() => setOpen(true)}
        className="gap-1 text-xs h-8 border-[#A86A3A]/30 text-[#A86A3A] hover:bg-[#FFF8F0]"
        data-testid="meeting-prep-btn"
      >
        <Sparkles className="w-3 h-3" /> AI Meeting Prep
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="meeting-prep-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#A86A3A]" />
              Meeting Prep — {clientName}
            </DialogTitle>
          </DialogHeader>

          {!brief && (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm text-[#5D524D] mb-2 block">Meeting type</label>
                <Select value={meetingType} onValueChange={setMeetingType}>
                  <SelectTrigger data-testid="meeting-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MEETING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-[#8C8078]">
                Generates a one-page prep brief from this client's data: contacts, open tasks, recent activity, handover notes, and upcoming reviews.
              </p>
            </div>
          )}

          {brief && (
            <div className="space-y-4 py-2 text-sm">
              <div className="bg-gradient-to-br from-[#FFF8F0] to-white border-l-4 border-[#A86A3A] p-4 rounded">
                <p className="text-[#2C2420] leading-relaxed">{brief.summary}</p>
              </div>

              {brief.keyContacts?.length > 0 && (
                <Section icon={Users} title="Key Contacts">
                  <div className="space-y-2">
                    {brief.keyContacts.map((c, i) => (
                      <div key={i} className="flex items-start gap-3 p-2 rounded bg-stone-50">
                        <div className="flex-1">
                          <p className="font-medium text-[#2C2420]">{c.name}</p>
                          <p className="text-[10px] text-[#8C8078]">{c.role}</p>
                          {c.note && <p className="text-xs text-[#5D524D] mt-1">{c.note}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {brief.openItems?.length > 0 && (
                <Section icon={Calendar} title="Open Items">
                  <ul className="space-y-1.5">
                    {brief.openItems.map((o, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs">
                        <Badge className="text-[9px]">{o.status}</Badge>
                        <span className="text-[#2C2420] flex-1">{o.title}</span>
                        {o.dueDate && <span className="text-[#8C8078]">{o.dueDate}</span>}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {brief.talkingPoints?.length > 0 && (
                <Section icon={MessageSquare} title="Talking Points">
                  <ul className="list-disc pl-5 space-y-1 text-[#2C2420]">
                    {brief.talkingPoints.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </Section>
              )}

              {brief.risks?.length > 0 && (
                <Section icon={AlertCircle} title="Risks" tone="amber">
                  <ul className="list-disc pl-5 space-y-1 text-[#5D524D]">
                    {brief.risks.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </Section>
              )}

              {brief.recentActivity?.length > 0 && (
                <Section icon={Calendar} title="Recent Activity">
                  <ul className="list-disc pl-5 space-y-1 text-[#5D524D] text-xs">
                    {brief.recentActivity.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </Section>
              )}

              {brief.suggestedQuestions?.length > 0 && (
                <Section icon={HelpCircle} title="Suggested Questions">
                  <ul className="list-disc pl-5 space-y-1 text-[#2C2420]">
                    {brief.suggestedQuestions.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </Section>
              )}
            </div>
          )}

          <DialogFooter>
            {!brief ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  onClick={generate}
                  disabled={loading}
                  className="bg-[#4A3B32] text-white hover:bg-[#3a2e26]"
                  data-testid="meeting-prep-generate-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Generate Brief
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setBrief(null)}>Regenerate</Button>
                <Button onClick={printBrief} className="gap-2" data-testid="meeting-prep-print-btn">
                  <Printer className="w-4 h-4" /> Print
                </Button>
                <Button onClick={() => setOpen(false)} className="bg-[#4A3B32] text-white hover:bg-[#3a2e26]">Done</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ icon: Icon, title, tone = 'default', children }) {
  const toneColor = tone === 'amber' ? 'text-amber-700' : 'text-[#4A3B32]';
  return (
    <div>
      <h4 className={`text-[10px] uppercase tracking-wide font-medium mb-2 flex items-center gap-1 ${toneColor}`}>
        <Icon className="w-3 h-3" /> {title}
      </h4>
      {children}
    </div>
  );
}
